<template>
  <div :class="['tabs', sizeClass, variantClass, $attrs.class]">
    <slot />
  </div>
</template>

<script setup>
import { provide, computed } from 'vue';

defineOptions({
  name: 'AppTabs',
});

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'border', 'button'].includes(value),
  },
});

const sizeClass = computed(() => ({
  'tabs-md': props.size === 'md',
  'tabs-lg': props.size === 'lg',
}));

const variantClass = computed(() => ({
  'tabs-default': props.variant === 'default',
  'tabs-border border-b border-gray-200': props.variant === 'border',
  'tabs-button': props.variant === 'button',
}));

const emit = defineEmits(['update:modelValue']);

provide('tabs', {
  name: props.name,
  activeTab: computed(() => props.modelValue),
  setActiveTab: (value) => emit('update:modelValue', value),
});
</script>

<style>
@reference '@/assets/main.css';

.tabs {
  --gap: calc(var(--spacing) * 3);
  @apply inline-flex gap-x-[var(--gap)];
}

.tab {
  @apply inline-flex items-center gap-2 font-semibold transition-all duration-100 min-w-14;

  &:not(:checked, label:has(:checked), :hover, .tab-active, [aria-selected='true']) {
    color: var(--color-gray-500);
  }
}

.tabs-lg {
  & .tab {
    @apply text-base;
  }
}

.tabs-default {
  --gap: var(--spacing);

  & .tab {
    @apply h-auto px-3 py-2 rounded-md;

    &:is(.tab-active, [aria-selected='true']):not(.tab-disabled, [disabled]),
    &:is(input:checked),
    &:is(label:has(:checked)) {
      @apply bg-gray-50;
    }
  }
}

.tabs-button {
  --gap: var(--spacing);
  @apply bg-gray-50 rounded-lg ring-1 ring-gray-200 p-1;

  & .tab {
    @apply h-auto px-3 py-2 rounded-md;

    &:is(.tab-active, [aria-selected='true']):not(.tab-disabled, [disabled]),
    &:is(input:checked),
    &:is(label:has(:checked)) {
      @apply bg-white shadow ring-1 ring-gray-200;
    }
  }
}

.tabs-border {
  @apply gap-x-3;

  & .tab {
    @apply h-auto px-1 pb-3 text-brand-700 hover:text-brand-700;

    &:before {
      @apply left-0 h-[2px] w-full;
    }

    &:hover,
    &:is(.tab-active, [aria-selected='true']):not(.tab-disabled, [disabled]),
    &:is(input:checked),
    &:is(label:has(:checked)) {
      &:before {
        border-top: 2px solid;
      }
    }
  }
}
</style>
